export interface RGBA {
  r: number
  g: number
  b: number
  a: number
}

function isHexColor(hex: string): boolean {
  const HEX_REGEXP = /^#?([0-9A-F]{3}){1,2}$/i
  return HEX_REGEXP.test(hex)
}

function isHexaColor(hexa: string): boolean {
  const HEXA_REGEXP = /^#?([0-9A-F]{4}){1,2}$/i
  return HEXA_REGEXP.test(hexa)
}

function hexToRgba(color: string): RGBA {
  let hexString = color.replace('#', '')

  if (hexString.length === 3) {
    const shorthandHex = hexString.split('')
    hexString = [
      shorthandHex[0],
      shorthandHex[0],
      shorthandHex[1],
      shorthandHex[1],
      shorthandHex[2],
      shorthandHex[2],
    ].join('')
  }

  const parsed = parseInt(hexString, 16)
  const r = (parsed >> 16) & 255
  const g = (parsed >> 8) & 255
  const b = parsed & 255

  return {
    r,
    g,
    b,
    a: 1,
  }
}

function hexaToRgba(color: string): RGBA {
  const hexString = color.replace('#', '')
  const parsed = parseInt(hexString, 16)
  const r = (parsed >> 24) & 255
  const g = (parsed >> 16) & 255
  const b = (parsed >> 8) & 255
  const a = Math.round(((parsed & 255) * 100) / 255) / 100

  return {
    r,
    g,
    b,
    a,
  }
}

function rgbStringToRgba(color: string): RGBA {
  const [r, g, b, a] = color
    .replace(/[^0-9,.]/g, '')
    .split(',')
    .map(Number) as [number, number, number, number | undefined]

  return { r, g, b, a: a ?? 1 }
}

export function toRgba(color: string): RGBA {
  if (isHexColor(color)) {
    return hexToRgba(color)
  }
  if (isHexaColor(color)) {
    return hexaToRgba(color)
  }
  if (color.startsWith('rgb')) {
    return rgbStringToRgba(color)
  }
  return {
    r: 0,
    g: 0,
    b: 0,
    a: 1,
  }
}

export function toOpaqueColor(color: string): string {
  if (color.startsWith('var(')) {
    return ''
  }

  const { r, g, b } = toRgba(color)
  return `rgb(${r}, ${g}, ${b})`
}

export function getColorBrightness(color: string, bgRgb: Omit<RGBA, 'a'> = { r: 255, g: 255, b: 255 }) {
  const { r, g, b, a } = toRgba(color)
  const rComp = (1 - a) * bgRgb.r + a * r
  const gComp = (1 - a) * bgRgb.g + a * g
  const bComp = (1 - a) * bgRgb.b + a * b

  return rComp * 0.299 + gComp * 0.587 + bComp * 0.114
}

export function lighten(color: string, alpha: number) {
  if (color.startsWith('var(')) {
    return color
  }

  const { r, g, b, a } = toRgba(color)
  const light = (input: number) => Math.round(input + (255 - input) * alpha)
  return `rgba(${light(r)}, ${light(g)}, ${light(b)}, ${a})`
}

export function darken(color: string, alpha: number) {
  if (color.startsWith('var(')) {
    return `color-mix(in srgb, ${color}, black ${alpha * 100}%)`
  }

  const { r, g, b, a } = toRgba(color)
  const f = 1 - alpha

  const dark = (input: number) => Math.round(input * f)

  return `rgba(${dark(r)}, ${dark(g)}, ${dark(b)}, ${a})`
}

export function isLightColor(color: string, luminanceThreshold = 0.179) {
  const rgba = toRgba(color)
  const isLight = getLuminance(rgba) > luminanceThreshold
  return isLight && rgba.a > 0.45
}

export function getLuminance(input: { r: number; g: number; b: number }, usePartLuminance = true) {
  const { r, g, b } = usePartLuminance
    ? { r: getPartLuminance(input.r), g: getPartLuminance(input.g), b: getPartLuminance(input.b) }
    : input
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function getPartLuminance(value: number) {
  const x = value / 255
  return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4
}
